import React, { useState, useEffect } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import { Bars } from "react-loader-spinner";
import axiosInstance from "../../Shared/axiosInstance";
import { BASE_URL } from "../../Shared/URL.js";

export default function BulkPaymentForm() {
  const [customers, setCustomers] = useState([]);
  const [customerSearch, setCustomerSearch] = useState("");
  const [loadingCustomers, setLoadingCustomers] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const navigate = useNavigate();

  // ✅ جلب قائمة العملاء للبحث
  useEffect(() => {
    const fetchCustomers = async () => {
      try {
        setLoadingCustomers(true);
        const { data } = await axiosInstance.get(`${BASE_URL}/Customer/all`, {
          params: { name: customerSearch || undefined, pageNumber: 1, pageSize: 15 },
        });
        setCustomers(data.data || []);
      } catch (err) {
        console.error("فشل تحميل العملاء", err);
      } finally {
        setLoadingCustomers(false);
      }
    };
    const delay = setTimeout(fetchCustomers, 400);
    return () => clearTimeout(delay);
  }, [customerSearch]);

  const paymentMethods = [
    { value: 0, label: "💵 نقدي" },
    { value: 1, label: "📱 فودافون كاش" },
    { value: 2, label: "💳 إنستاباي" },
  ];

  const formik = useFormik({
    initialValues: {
      customerId: "",
      amount: "",
      date: new Date().toISOString().split("T")[0],
      method: "",
    },
    validationSchema: Yup.object({
      customerId: Yup.string().required("يجب اختيار العميل"),
      amount: Yup.number()
        .required("المبلغ مطلوب")
        .positive("يجب أن يكون مبلغاً موجباً")
        .min(0.1, "المبلغ صغير جداً"),
      date: Yup.string().required("التاريخ مطلوب"),
      method: Yup.string().required("اختر طريقة الدفع"),
    }),
    onSubmit: async (values) => {
      setIsSubmitting(true);
      try {
        const body = {
          customerId: values.customerId,
          amount: parseFloat(values.amount),
          date: values.date,
          method: parseInt(values.method),
        };

        // الـ Endpoint الجديد الخاص بك
        const { data } = await axiosInstance.post("/Payment/bulk-payment", body);

        await Swal.fire({
          title: "تم التسجيل!",
          text: data.message || "تم إضافة الدفع المجمع بنجاح",
          icon: "success",
          timer: 2000,
          showConfirmButton: false,
        });

        navigate("/sales"); // أو الصفحة التي تفضلها
      } catch (error) {
        Swal.fire("خطأ", error?.response?.data?.message || "حدث خطأ أثناء الحفظ", "error");
      } finally {
        setIsSubmitting(false);
      }
    },
  });

  return (
    <div style={{ background: "#f8fafc", minHeight: "100vh", paddingBottom: "40px" }}>
      <div className="container py-5" style={{ maxWidth: "800px" }}>
        
        {/* Header */}
        <div className="d-flex justify-content-between align-items-center mb-4 mt-5 pt-5">
          <div>
            <h2 style={{ color: "#2563eb", fontWeight: "700", fontSize: "28px" }}>
              <i className="bi bi-wallet2 me-2"></i>دفع مجمع (حساب عميل)
            </h2>
            <small style={{ color: "#64748b" }}>تسجيل مبلغ سيتم توزيعه على فواتير العميل تلقائياً</small>
          </div>
          <button className="btn btn-secondary" onClick={() => navigate(-1)}>رجوع</button>
        </div>

        <div style={cardStyle}>
          <form onSubmit={formik.handleSubmit}>
            
            {/* Customer Search & Select */}
            <div className="mb-4">
              <label style={labelStyle}>العميل</label>
              <input
                type="text"
                className="form-control mb-2"
                placeholder="🔍 ابحث عن العميل بالاسم..."
                value={customerSearch}
                onChange={(e) => setCustomerSearch(e.target.value)}
              />
              <select
                name="customerId"
                className={`form-select ${formik.touched.customerId && formik.errors.customerId ? "is-invalid" : ""}`}
                value={formik.values.customerId}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
              >
                <option value="">-- اختر العميل المستهدف --</option>
                {customers.map((c) => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
              {formik.touched.customerId && formik.errors.customerId && (
                <div style={errorStyle}>{formik.errors.customerId}</div>
              )}
            </div>

            <div className="row">
              {/* Amount */}
              <div className="col-md-6 mb-4">
                <label style={labelStyle}>المبلغ الكلي</label>
                <input
                  type="number"
                  name="amount"
                  className={`form-control ${formik.touched.amount && formik.errors.amount ? "is-invalid" : ""}`}
                  placeholder="0.00"
                  value={formik.values.amount}
                  onChange={formik.handleChange}
                />
                {formik.touched.amount && formik.errors.amount && (
                  <div style={errorStyle}>{formik.errors.amount}</div>
                )}
              </div>

              {/* Date */}
              <div className="col-md-6 mb-4">
                <label style={labelStyle}>تاريخ الدفع</label>
                <input
                  type="date"
                  name="date"
                  className="form-control"
                  value={formik.values.date}
                  onChange={formik.handleChange}
                />
              </div>
            </div>

            {/* Method */}
            <div className="mb-4">
              <label style={labelStyle}>طريقة الدفع</label>
              <div className="d-flex gap-3">
                {paymentMethods.map((m) => (
                  <label key={m.value} style={{
                    flex: 1,
                    padding: "12px",
                    border: formik.values.method == m.value ? "2px solid #2563eb" : "1px solid #cbd5e1",
                    borderRadius: "8px",
                    cursor: "pointer",
                    textAlign: "center",
                    background: formik.values.method == m.value ? "#eff6ff" : "#fff"
                  }}>
                    <input
                      type="radio"
                      name="method"
                      value={m.value}
                      className="d-none"
                      onChange={formik.handleChange}
                    />
                    {m.label}
                  </label>
                ))}
              </div>
              {formik.touched.method && formik.errors.method && (
                <div style={errorStyle}>{formik.errors.method}</div>
              )}
            </div>

            {/* Submit */}
            <button
              type="submit"
              disabled={isSubmitting || !formik.isValid}
              style={{
                ...submitButtonStyle,
                background: isSubmitting ? "#94a3b8" : "linear-gradient(135deg, #2563eb 0%, #1e40af 100%)"
              }}
            >
              {isSubmitting ? "جارٍ الحفظ..." : "تأكيد الدفع المجمع"}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}

// التنسيقات (نفس الـ Style الخاص بك لضمان التناسق)
const cardStyle = {
  background: "#fff",
  borderRadius: "12px",
  padding: "30px",
  boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
  border: "1px solid #e2e8f0",
};

const labelStyle = {
  display: "block",
  marginBottom: "8px",
  fontWeight: "600",
  color: "#1e293b",
};

const errorStyle = {
  color: "#dc2626",
  fontSize: "13px",
  marginTop: "4px",
};

const submitButtonStyle = {
  width: "100%",
  color: "#fff",
  padding: "16px",
  borderRadius: "8px",
  border: "none",
  fontWeight: "700",
  fontSize: "18px",
  cursor: "pointer",
  transition: "all 0.3s ease",
};